import React, { useEffect, useState } from 'react'
import { Button, notification, Switch } from 'antd'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'

import { addAlert, fetchAllAlerts, resetAlertsActionStatus } from '~actions'
import { ResultModal, AlertInfo } from '~components'
import { getCurrentUser } from '~context'
import { PageLayout, Spinner } from '~stories'

import styles from './NewGlobalAlert.module.css'

const PAGE_TITLE = 'New Global Alert'
const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.'
}

const NewGlobalAlert = ({
  addAlert,
  allAlerts,
  allVenues,
  fetchAllAlerts,
  newAlertId,
  updateMessage,
  updateStatus
}) => {
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [alert, setAlert] = useState({
    message: '',
    url: '',
    active: true,
    type: 'info',
    color: '#9ACBFF',
    schedule: {
      start_date: null,
      end_date: null
    },
    cookie_expiration_date: null,
    venue_id: '',
    show_alert_on_promo: true,
    icon: {
      url: '',
      alt: ''
    }
  })

  useEffect(() => {
    fetchAllAlerts().finally(() => setIsDataLoaded(true))
  }, [fetchAllAlerts])

  const handleSubmit = () => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    addAlert({
      ...alert
    }, accessToken)
  }

  const handleMessageChange = htmlDescription => {
    setAlert({
      ...alert,
      message: htmlDescription
    })
  }

  const handleAlertStatus = status => {
    setAlert({
      ...alert,
      active: status
    })
  }
  const handleUrlUpdate = ({ target }) => {
    setAlert({
      ...alert,
      url: target.value
    })
  }

  // Derive color from type if color is not set (backward compatibility)
  const getColorValue = (color) => {
    if (color === '#9acbff') return '#9ACBFF'
    if (color === '#da0d50') return '#DA0D50'
    if (color === '#7827aa') return '#7827AA'
    return undefined
  }

  const handleTypeChange = (color) => {
    setAlert({
      ...alert,
      color: getColorValue(color) || ''
    })
  }

  const handleStartDateChange = (date) => {
    if (!date) {
      setAlert({
        ...alert,
        schedule: {
          ...alert?.schedule,
          start_date: null
        }
      })
      return
    }

    // Get venue timezone
    const selectedVenue = allVenues.find(v => v.value === alert?.venue_id)
    const venueTimezone = selectedVenue?.timezone || 'America/New_York'

    // Convert date to Unix timestamp based on venue timezone
    const dateInVenueTz = moment.tz(
      date.format('YYYY-MM-DD HH:mm:ss'),
      venueTimezone
    )

    setAlert({
      ...alert,
      schedule: {
        ...alert?.schedule,
        start_date: dateInVenueTz.unix()
      }
    })
  }

  const handleEndDateChange = (date) => {
    if (!date) {
      setAlert({
        ...alert,
        schedule: {
          ...alert?.schedule,
          end_date: null
        }
      })
      return
    }

    // Get venue timezone
    const selectedVenue = allVenues.find(v => v.value === alert?.venue_id)
    const venueTimezone = selectedVenue?.timezone || 'America/New_York'

    // Convert date to Unix timestamp based on venue timezone
    const dateInVenueTz = moment.tz(
      date.format('YYYY-MM-DD HH:mm:ss'),
      venueTimezone
    )

    // Validate that end date is greater than start date
    if (alert?.schedule?.start_date) {
      const startDate = typeof alert.schedule.start_date === 'number'
        ? moment.unix(alert.schedule.start_date)
        : moment(alert.schedule.start_date)
      const endDate = dateInVenueTz

      if (!endDate.isAfter(startDate)) {
        notification.error({
          message: 'Validation Error',
          description: 'End date must be greater than start date'
        })
        return // Don't update state
      }
    }

    setAlert({
      ...alert,
      schedule: {
        ...alert?.schedule,
        end_date: dateInVenueTz.unix()
      }
    })
  }

  const handleCookieExpirationChange = (date) => {
    setAlert({
      ...alert,
      cookie_expiration_date: date ? date.unix() : null
    })
  }

  const handleVenueChange = (venueId) => {
    setAlert({
      ...alert,
      venue_id: venueId
    })
  }

  const handleShowAlertOnPromoChange = (e) => {
    setAlert({
      ...alert,
      show_alert_on_promo: e.target.checked || false
    })
  }

  const handleModalClose = () => {
    resetAlertsActionStatus()
  }

  const addAlertsButton = (
    <Button
      onClick={() => {
        resetAlertsActionStatus()
        window.location.reload()
      }}
      type="primary"
    >
      Add Another
    </Button>
  )

  const goToListAlertsButton = (
    <Route key="listAlerts" render={({ history }) => (
      <Button
        onClick={() => {
          resetAlertsActionStatus()
          history.push('/global-alerts')
          window.location.reload()
        }}
        type="primary"
      >
        Go to Alerts List
      </Button>
    )} />
  )

  const editAlert = (
    <Route key="editAlert" render={({ history }) => (

      <Button
        onClick={() => {
          newAlertId && history.push(`/global-alerts/${newAlertId}/edit`)
          window.location.reload()
        }}
        type="primary"
      >
        Keep Editing
      </Button>
    )} />
  )

  const handleIconChange = (imgObj) => {
    const clonedAlert = { ...alert }
    clonedAlert.icon = { ...clonedAlert.icon, ...imgObj }
    setAlert(clonedAlert)
  }

  const handleImageUrlUpdate = (imageUrl) => {
    const clonedAlert = { ...alert }
    clonedAlert.icon = { ...clonedAlert.icon, url: imageUrl }
    setAlert(clonedAlert)
  }

  const addAlertButton = <Button onClick={handleSubmit} type='primary'>
    Add
  </Button>

  const toggleSwitch = <Switch checked={alert?.active} checkedChildren="Show" defaultChecked onChange={handleAlertStatus} unCheckedChildren="Hide" />

  if (!isDataLoaded) return <Spinner isLoading />

  return (
    <PageLayout extra={[toggleSwitch, addAlertButton]} title={PAGE_TITLE}>
      <div className={styles.AlertsLayoutBackground}>
        <AlertInfo
          alert={alert}
          allVenues={allVenues}
          existingAlerts={allAlerts || []}
          handleCookieExpirationChange={handleCookieExpirationChange}
          handleEndDateChange={handleEndDateChange}
          handleIconChange={handleIconChange}
          handleImageUrlUpdate={handleImageUrlUpdate}
          handleMessageChange={handleMessageChange}
          handleShowAlertOnPromoChange={handleShowAlertOnPromoChange}
          handleStartDateChange={handleStartDateChange}
          handleTypeChange={value => handleTypeChange(value?.toLowerCase() || '')}
          handleUrlUpdate={handleUrlUpdate}
          handleVenueChange={handleVenueChange}
        />
      </div>
      {
        updateStatus &&
        <ResultModal
          actions={[
            editAlert,
            goToListAlertsButton,
            addAlertsButton
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={updateMessage}
        />
      }
    </PageLayout>
  )
}

const mapStateToProps = ({ alerts }) => ({
  allVenues: alerts.allVenues || [],
  allAlerts: alerts.allAlerts || [],
  newAlertId: alerts.newAlertId,
  updateMessage: alerts.message,
  updateStatus: alerts.success
})

const mapDispatchToProps = {
  addAlert,
  fetchAllAlerts
}

NewGlobalAlert.propTypes = {
  addAlert: PropTypes.func,
  allAlerts: PropTypes.array,
  allVenues: PropTypes.array,
  fetchAllAlerts: PropTypes.func,
  newAlertId: PropTypes.string,
  updateMessage: PropTypes.string,
  updateStatus: PropTypes.bool
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NewGlobalAlert)
